import { useState, type FormEvent } from 'react';
import { KeyIcon } from '../components/icons';
import { defaultApiUrl, normalizeApiUrl } from '../lib/config';

interface SetupScreenProps {
  initialApiUrl: string;
  onSave: (kioskKey: string, apiUrl: string | null) => void;
}

/** First-run pairing screen for the technician. */
export function SetupScreen({ initialApiUrl, onSave }: SetupScreenProps) {
  const [key, setKey] = useState('');
  const [apiUrl, setApiUrl] = useState(initialApiUrl);
  const [showAdvanced, setShowAdvanced] = useState(initialApiUrl !== defaultApiUrl());
  const [error, setError] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = key.trim();
    if (trimmed.length < 8) {
      setError('Paste the full kiosk key from the owner dashboard.');
      return;
    }
    const api = normalizeApiUrl(apiUrl);
    if (!api) {
      setError('API URL must start with http:// or https://');
      return;
    }
    onSave(trimmed, api === defaultApiUrl() ? null : api);
  };

  const field =
    'w-full rounded-[1.6vmin] bg-ink px-[2.4vmin] py-[1.8vmin] font-mono text-[2.4vmin] text-text ring-2 ring-line outline-none placeholder:text-muted/50 focus:ring-accent';

  return (
    <div className="grid h-full w-full place-items-center overflow-auto p-[4vmin]">
      <form
        onSubmit={submit}
        className="w-[min(100vmin,94vw)] rounded-[3vmin] bg-ink-2 p-[5vmin] ring-2 ring-line"
      >
        <div className="flex items-center gap-4">
          <div className="grid size-[9vmin] place-items-center rounded-[2vmin] bg-accent/15 text-accent">
            <KeyIcon className="size-[5.5vmin]" />
          </div>
          <div>
            <h1 className="text-[4.4vmin] leading-tight font-extrabold">Pair this screen</h1>
            <p className="text-[2.2vmin] text-muted">Technician setup — one time only</p>
          </div>
        </div>

        <ol className="mt-[3vmin] list-decimal space-y-1 pl-[3vmin] text-[2.2vmin] text-muted">
          <li>Open the owner dashboard → Kiosks → this kiosk.</li>
          <li>Copy the kiosk key (or rotate it to get a new one).</li>
          <li>Paste it below. Tip: you can also open this page with <code>?key=…</code></li>
        </ol>

        <label className="mt-[3vmin] block">
          <span className="mb-2 block text-[2.2vmin] font-semibold">Kiosk key</span>
          <input
            className={field}
            value={key}
            onChange={(e) => {
              setKey(e.target.value);
              setError(null);
            }}
            autoFocus
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder="Paste key here"
          />
        </label>

        {showAdvanced ? (
          <label className="mt-[2.4vmin] block">
            <span className="mb-2 block text-[2.2vmin] font-semibold">API URL</span>
            <input
              className={field}
              value={apiUrl}
              onChange={(e) => {
                setApiUrl(e.target.value);
                setError(null);
              }}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              inputMode="url"
            />
          </label>
        ) : (
          <button
            type="button"
            onClick={() => setShowAdvanced(true)}
            className="mt-[2vmin] text-[2vmin] text-muted underline underline-offset-4"
          >
            Change API server ({initialApiUrl})
          </button>
        )}

        {error && <p className="mt-[2vmin] text-[2.2vmin] font-semibold text-bad">{error}</p>}

        <button
          type="submit"
          className="mt-[3.5vmin] w-full rounded-[1.8vmin] bg-accent py-[2.2vmin] text-[3vmin] font-extrabold text-accent-ink active:brightness-90"
        >
          Save & connect
        </button>
      </form>
    </div>
  );
}
